import axios from "axios";
import { useState } from "react";
import axiosApi from "./axiosInstance";
import { axiosErrorHandler } from "./axiosErrorHandler";

// Every helper accepts an optional trailing `{ signal }` so a caller (usually a screen
// via useAbortSignal) can cancel the request when it unmounts. Without a signal the
// request simply runs to completion, as before.
async function send(config, options = {}) {
  return axiosApi
    .request({ ...config, signal: options.signal })
    .then((res) => res.data)
    .catch((err) => {
      throw axiosErrorHandler(err);
    });
}

export function axiosPost(url, params = null, options) {
  return send({ url, method: "post", data: params }, options);
}
export function axiosPostWithParamBody(url, params = null, body = null, options) {
  return send({ url, method: "post", params, data: body }, options);
}
export function axiosGet(url, params = null, options) {
  return send({ url, method: "get", params }, options);
}
export function axiosPut(url, params = null, options) {
  return send({ url, method: "put", params }, options);
}
export function axiosPutWithParamBody(url, params = null, body = null, options) {
  return send({ url, method: "put", params, data: body }, options);
}
export function axiosDelete(url, params = null, options) {
  return send({ url, method: "delete", params }, options);
}
export function axiosPostFormData(url, files = null, options) {
  return send(
    {
      url,
      method: "post",
      data: files,
      headers: { "Content-Type": "multipart/form-data" },
    },
    options,
  );
}

/** True when the error came from a request cancelled through its AbortSignal. */
export function isRequestCancelled(error) {
  return axios.isCancel(error) || /^Request cancelled/.test(error?.message ?? "");
}

export function useAxiosApi() {
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const CancelToken = axios.CancelToken;
  let cancel;

  const post = async (url, params = null) => {
    setLoading(true);
    await axiosApi
      .post(url, params, {
        cancelToken: new CancelToken(function executor(c) {
          cancel = c;
        }),
      })
      .then((res) => setResponse(res.data))
      .catch((err) => {
        setError(axiosErrorHandler(err).message);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const get = async (url, params = null) => {
    setLoading(true);
    await axiosApi
      .get(url, {
        params,
        cancelToken: new CancelToken(function executor(c) {
          cancel = c;
        }),
      })
      .then((res) => setResponse(res.data))
      .catch((err) => {
        setError(axiosErrorHandler(err).message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return { loading, response, error, post, get, cancel };
}
